import React, { useContext, useState, useEffect } from "react";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftAvatar from "components/SoftAvatar";
import Table from "examples/Tables/Table";
import swal from "sweetalert2";
import AuthContext from "context/AuthContext";
import Card from "@mui/material/Card";
import bruce from "assets/images/bruce-mars.jpg";
import { api } from "utils/Api";
import { Create } from "@mui/icons-material";
import CreateOrganizationPopup from "./EditForm";
import DeleteForm from "./DeleteForm";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SmartTableCard from "utils/Smart Table/SmartTableCard";
import { useNavigate } from "react-router-dom";
import { useHierarchy } from "context/HierarchyContext";
import { showSuccess } from "utils/messageAlerts";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { Tooltip } from "@mui/material";
import ConfirmDialog from "utils/ConfirmDialog";

const OrganizationData = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupMode, setPopupMode] = useState("create"); // "create" or "edit"
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const { setOrgId } = useHierarchy();
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null,
    org: null,
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  // handle click to navigate to organization details
  const handleClickOrg = (org) => {
    setOrgId(org.id);
    navigate(`/super-admin/organization/${org.organization_code}`);
  }

  // Fetch Organizations
  const fetchOrganizations = async () => {
    try {
      const organizationData = await api.su_Organiation.getSuOrgs();
      const formatted = organizationData.map((org) => ({
        id: org.id,
        name: org.name,
        email: org.email,
        description: org.description,
        active: org.active,
        createdAt: org.created_at,
        organization_code: org.organization_code,
      }));

      setOrganizations(formatted);
      setFilteredOrganizations(formatted);
    } catch (e) {
      setOrganizations([]);
    }
  };

  // Edit Organization - to be implemented
const handleEditOrganization = (org) => {
  setPopupMode("edit");
  setSelectedOrg(org);
  setIsPopupOpen(true);
};

  //  Delete Organization
  const handleDeleteOrganization = async (organizationId) => {
    try {
      await api.su_Organiation.deleteSuOrg(organizationId);

      showSuccess("Organization deleted successfully");

      fetchOrganizations();
    } catch (err) {
    }
  };

  // Search Organizations
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (!value.trim()) {
      setFilteredOrganizations(organizations);
      return;
    }

    const filtered = organizations.filter((org) =>
      org.name.toLowerCase().includes(value.toLowerCase()) ||
      org.email.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredOrganizations(filtered);
  };

   const handlePropertyActionClick = (org, action) => {
    setConfirmDialog({
      open: true,
      type: action,
      org,
    });
  };

  const handleOrganizationActive = async () => {
    const {type, org } = confirmDialog;
      const isDelete = type === "delete";

      try {
            if (isDelete) {
              await api.su_Organiation.hardDeleteOrg(org.id);
              showSuccess("Organization permanently deleted successfully");
            } else {
              await api.su_Organiation.restoreOrg({
                organization_id: org.id,
              });
              showSuccess("Organization restored successfully");
            }
      
            fetchOrganizations();
          } catch (error) {
            console.error("Error updating organization status:", error);}
      
          setConfirmDialog({ open: false, type: null, org: null });
  };
    
  return (
    <SmartTableCard
      title={`All Organizations`}
      headerRight={[
        <SoftButton
          key="add-btn"
          variant="outlined"
          color="info"
          size="small"
          onClick={() => {
            setPopupMode("create");
            setSelectedOrg(null);
            setIsPopupOpen(true);
          }}
        >
          Add Organization
        </SoftButton>,

        <SoftInput
          key="search-input"
          placeholder="Search Organization"
          value={searchTerm}
          onChange={handleSearch}
          size="small"
        />,
      ]}
      columns={[
        { label: "Organization", key: "Organization", align: "center" },
        { label: "Organization Code", key: "OrganizationCode", align: "center" },
        { label: "Email", key: "Email", align: "center" },
        { label: "Description", key: "Description", align: "center" },
        { label: "Created", key: "Created", align: "center" },
        { label: "Active", key: "Active", align: "center" },
        { label: "Actions", key: "Actions", align: "center" },
      ]}
      rows={filteredOrganizations.map((org) => {
        const isActive = org.active === true;
        const isInactive = !isActive;
        return {
        Organization: (
          <SoftBox display="flex" alignItems="center">
            <SoftBox>
              <SoftTypography
                variant="button"
                sx={{
                  cursor: "pointer",
                  fontWeight: 600}}
                onClick={() => handleClickOrg(org)}>
                {org.name}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        ),

        OrganizationCode: org.organization_code,
        Email: org.email,
        Description: org.description,
        Created: new Date(org.createdAt).toLocaleDateString(),
        Active: isActive ? (
          <SoftTypography variant="button" color="success" fontWeight="bold">
            Yes
          </SoftTypography>
        ) : (
          <SoftBox display="flex" justifyContent="center" gap="6px">
            <Tooltip title="Restore Unit" arrow>
              <IconButton
                onClick={() => handlePropertyActionClick(org, "restore")}
                sx={{
                  width: 30,
                  height: 30,
                  color: "#16A34A",
                  backgroundColor: "#ECFDF5",
                  "&:hover": {
                    backgroundColor: "#D1FAE5",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <RestoreIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            {/* Delete */}
            <Tooltip title="Delete Permanently" arrow>
              <IconButton
                onClick={() => handlePropertyActionClick(org, "delete")}
                sx={{
                  width: 30,
                  height: 30,
                  color: "#DC2626",
                  backgroundColor: "#FEF2F2",
                  "&:hover": {
                    backgroundColor: "#FEE2E2",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <DeleteForeverIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </SoftBox>
        ),
        Actions: (
          <SoftBox display="flex" justifyContent="center" gap="8px">
            <IconButton
              onClick={() => handleEditOrganization(org)}
              disabled={isInactive}
              sx={{
                border: "1px solid #D0D5DD",
                backgroundColor: "#F9FAFB",
                width: 34,
                height: 34,
                "& svg": { fontSize: 18 },
                color: "#475467",
                "&:hover": {
                  backgroundColor: "#E0F2FE",
                  borderColor: "#38BDF8",
                  color: "#0284C7",
                },
              }}
            >
              <EditIcon fontSize="sm" />
            </IconButton>

            <IconButton
              onClick={() => {
                setDeletePopupOpen(true);
                setSelectedOrg(org);
              }}
              disabled={isInactive}
              sx={{
                border: "1px solid #FECACA",
                backgroundColor: "#FEF2F2",
                width: 34,
                height: 34,
                "& svg": { fontSize: 18 },
                color: "#B91C1C",
                "&:hover": {
                  backgroundColor: "#FEE2E2",
                  borderColor: "#F87171",
                  color: "#991B1B",
                },
              }}
            >
              <DeleteIcon fontSize="sm" />
            </IconButton>
          </SoftBox>
        ),
      }})}
    >

      <ConfirmDialog
        open={confirmDialog.open}
        type={confirmDialog.type}
        title={confirmDialog.type === "delete" ? "Delete Organization" : "Restore Organization"}
        description={
          confirmDialog.type === "delete"
            ? "This will permanently delete the Organization. This action cannot be undone."
            : "Are you sure you want to restore this Organization?"
        }
        confirmText={confirmDialog.type === "delete" ? "Delete Permanently" : "Restore"}
        onClose={() => setConfirmDialog({ open: false, type: null, org: null })}
        onConfirm={handleOrganizationActive}
      />

      {isPopupOpen && (
        <CreateOrganizationPopup
          mode={popupMode}
          organization={selectedOrg}
          refreshOrganizations={fetchOrganizations}
          onClose={() => {
            setIsPopupOpen(false);
          }}
        />
      )}

      {deletePopupOpen && (
        <DeleteForm
          data={selectedOrg}
          onClose={() => setDeletePopupOpen(false)}
          onDelete={(id) => {
            handleDeleteOrganization(id);
            setDeletePopupOpen(false);
          }}
        />
      )}
    </SmartTableCard>
  );
}

export default OrganizationData;